import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
// import { shouldUseIframe } from "../../../utils/shouldUseIframe";
import { FiPaperclip } from "react-icons/fi";
import { FaTimes } from "react-icons/fa";
import { GoGlobe } from "react-icons/go";
import IframeViewer from "../../../shared/ui/urlOpener/IFrameViewer";
import ResultsCard from "./ResultsCard";
import SuggestedQuestions from "./SuggestedQuestions";
import { chatService, authService } from "../../../shared/services/apis";
import ChatMarkdown from "../../../shared/ui/chatmarkdown/ChatMarkdown";
import { PiSidebarSimpleThin } from "react-icons/pi";

type Message = {
  id: string;
  sessionId: string;
  query: string;
  result?: string;
  responseStatus: "PENDING" | "SUCCESS" | "FAILURE";
  createdAt: string;
  updatedAt?: string;
  isWebSearch?: boolean;
  metadata?: {
    fileName?: string;
    fileSize?: number;
    mimeType?: string;
  };
  s3FileUrl?: string;
};

type Source = {
  page_number: number;
  url: string;
};

type Result = {
  query: string;
  summary: string;
  source_count: number;
  sources: Source[];
};

const QueryInputBox = () => {
  const location = useLocation();

  const chatData = location.state?.chatData;

  // Default session ID
  const DEFAULT_SESSION_ID = "d4d895c6-d7d1-4ebb-a54d-a3bebf7fc3e1";
  const sessionId = chatData?.chatId || DEFAULT_SESSION_ID;

  // Check if this is an initial chat (no previous messages)
  const isInitialChat =
    !chatData || !chatData.messages || chatData.messages.length === 0;

  // State management
  const [messages, setMessages] = useState<Message[]>(chatData?.messages || []);
  const [messageInput, setMessageInput] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [waitingForReply, setWaitingForReply] = useState(false);
  const [websearch, setWebSearch] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [iframeWidth, setIframeWidth] = useState<string | null>(null);
  // const [iframeUrl, setIframeUrl] = useState<string | null>(null);

  // Legacy state for compatibility
  const [definition, setDefinition] = useState("");
  const [results] = useState<Result | null>(null);
  const [loading] = useState(false);
  const [allResults] = useState<Result[]>([]);
  const [selectedUrl, setSelectedUrl] = useState<string>("");
  const [isOpen, setIsOpen] = useState(true);

  // Polling state
  const [pollingInterval, setPollingInterval] = useState<number | null>(null);
  const [lastMessageCount, setLastMessageCount] = useState<number>(
    chatData?.messages?.length || 0
  );
  const [isInitialized, setIsInitialized] = useState(!isInitialChat);

  const shouldScrollRef = useRef(false);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loaderRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const topRef = useRef<HTMLDivElement | null>(null);

  // Stable scroll to bottom helper - memoized to prevent recreation
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current && shouldScrollRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
      shouldScrollRef.current = false; // Reset flag after scrolling
    }
  }, []);

  // Stable debounced scroll - only recreate when scrollToBottom changes
  const debouncedScrollToBottom = useCallback(() => {
    if (shouldScrollRef.current) {
      requestAnimationFrame(() => {
        setTimeout(scrollToBottom, 50);
      });
    }
  }, [scrollToBottom]);

  // Check if there are pending messages
  const hasPendingMessages = useMemo(() => {
    return messages.some((msg) => msg.responseStatus === "PENDING");
  }, [messages]);

  // Function to load initial messages - ONLY for initial chats
  const loadInitialMessages = useCallback(async () => {
    if (isInitialized || !isInitialChat) return;

    try {
      console.log("Loading initial messages for new session:", sessionId);
      const chatMessages = await chatService.getMessages(sessionId);

      setMessages(chatMessages);
      setLastMessageCount(chatMessages.length);
      setIsInitialized(true);

      // Check if there are any pending messages that need polling
      const hasPending = chatMessages.some(
        (msg) => msg.responseStatus === "PENDING"
      );
      if (hasPending) {
        console.log("Found pending messages, starting polling...");
        setWaitingForReply(true);
        startPollingForReply(sessionId);
      }

      // Set scroll flag and trigger scroll after loading messages
      shouldScrollRef.current = true;
      setTimeout(() => {
        debouncedScrollToBottom();
      }, 100);
    } catch (err) {
      console.error("Failed to load initial messages:", err);
      setMessagesError("Failed to load chat messages");
      setIsInitialized(true);
    }
  }, [sessionId, isInitialized, isInitialChat, debouncedScrollToBottom]);

  // Function to check for new messages - FIXED to prevent unnecessary state updates
  const checkForNewMessages = useCallback(
    async (currentSessionId: string) => {
      try {
        console.log("Checking for new messages...");
        const chatMessages = await chatService.getMessages(currentSessionId);

        // Only update state if there are actually new messages
        if (chatMessages.length > lastMessageCount) {
          console.log(
            `Found ${chatMessages.length - lastMessageCount} new messages`
          );
          const newMessages = chatMessages.slice(lastMessageCount);
          const hasAIReply = newMessages.some(
            (msg) =>
              msg.responseStatus === "SUCCESS" ||
              msg.responseStatus === "FAILURE"
          );

          // Set scroll flag for new messages
          shouldScrollRef.current = true;

          setMessages(chatMessages);
          setLastMessageCount(chatMessages.length);

          if (hasAIReply) {
            setWaitingForReply(false);
            if (pollingInterval) {
              clearInterval(pollingInterval);
              setPollingInterval(null);
            }
            debouncedScrollToBottom();
            return true;
          }
        }

        // Important: Don't trigger state updates if no new messages
        return false;
      } catch (err) {
        console.error("Failed to check for new messages:", err);
        return false;
      }
    },
    [lastMessageCount, pollingInterval, debouncedScrollToBottom]
  );

  // Start polling for replies - with better cleanup
  const startPollingForReply = useCallback(
    (currentSessionId: string) => {
      // Clear existing polling
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }

      // Clear existing timeout
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }

      console.log("Starting polling for session:", currentSessionId);

      const interval = window.setInterval(async () => {
        const replyReceived = await checkForNewMessages(currentSessionId);
        if (replyReceived) {
          clearInterval(interval);
          setPollingInterval(null);
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
        }
      }, 2000);

      setPollingInterval(interval);

      // Set timeout to stop polling after 5 minutes
      pollingTimeoutRef.current = setTimeout(() => {
        clearInterval(interval);
        setPollingInterval(null);
        setWaitingForReply(false);
        console.log("Polling stopped due to timeout");
      }, 300000); // 5 minutes
    },
    [checkForNewMessages]
  );

  // Send message handler
  const sendMessage = useCallback(async () => {
    if (!messageInput.trim()) return;

    if (hasPendingMessages) {
      setMessagesError(
        "Please wait for the previous message to complete before sending a new one."
      );
      return;
    }

    const messageContent = messageInput.trim();
    setSendingMessage(true);
    setMessagesError(null);
    setMessageInput("");

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      sessionId: sessionId,
      query: messageContent,
      responseStatus: "PENDING",
      createdAt: new Date().toISOString(),
    };

    // Set scroll flag for new user message
    shouldScrollRef.current = true;

    setMessages((prev) => {
      const newMessages = [...prev, userMessage];
      setLastMessageCount(newMessages.length);
      return newMessages;
    });
    debouncedScrollToBottom();

    console.log("Sending message to session:", sessionId);

    try {
      await chatService.sendUserMessage(
        sessionId,
        messageContent,
        selectedFile || undefined,
        websearch,
        selectedFile
          ? {
              fileName: selectedFile.name,
              fileSize: selectedFile.size,
              mimeType: selectedFile.type,
            }
          : undefined
      );

      setSelectedFile(null);
      setFilePreview(null);

      setWaitingForReply(true);
      startPollingForReply(sessionId);
    } catch (err) {
      console.error("Failed to send message:", err);

      let errorMessage = "Failed to send message. Please try again.";
      if (err && typeof err === "object" && "response" in err) {
        const response = (err as any).response;
        if (response?.data?.message) {
          errorMessage = response.data.message;
        } else if (response?.statusText) {
          errorMessage = `${response.status}: ${response.statusText}`;
        }
      } else if (err && typeof err === "object" && "message" in err) {
        errorMessage = (err as Error).message;
      }

      if (selectedFile && errorMessage.toLowerCase().includes("file")) {
        errorMessage = `File upload failed: ${errorMessage}`;
      }

      setMessagesError(errorMessage);
      setMessageInput(messageContent);

      setMessages((prev) => {
        const filtered = prev.filter((msg) => msg.id !== userMessage.id);
        setLastMessageCount(filtered.length);
        return filtered;
      });
      setWaitingForReply(false);

      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    } finally {
      setSendingMessage(false);
    }
  }, [
    messageInput,
    sendingMessage,
    sessionId,
    hasPendingMessages,
    selectedFile,
    websearch,
    debouncedScrollToBottom,
    startPollingForReply,
    pollingInterval,
  ]);

  // File handling functions
  // const handleFileSelect = useCallback(
  //   (event: React.ChangeEvent<HTMLInputElement>) => {
  //     if (hasPendingMessages) {
  //       setMessagesError(
  //         "Please wait for the previous message to complete before uploading files."
  //       );
  //       event.target.value = "";
  //       return;
  //     }

  //     const file = event.target.files?.[0];
  //     if (file) {
  //       const maxSize = 100 * 1024 * 1024; // 100MB
  //       if (file.size > maxSize) {
  //         setMessagesError(
  //           `File size ${(file.size / (1024 * 1024)).toFixed(
  //             1
  //           )}MB exceeds maximum allowed size of 100MB`
  //         );
  //         event.target.value = "";
  //         return;
  //       }

  //       const allowedTypes = [
  //         "application/pdf",
  //         "application/msword",
  //         "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  //         "text/plain",
  //         "text/csv",
  //         "application/vnd.ms-excel",
  //         "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //         "image/jpeg",
  //         "image/jpg",
  //         "image/png",
  //         "image/gif",
  //         "image/webp",
  //         "image/svg+xml",
  //       ];

  //       if (!allowedTypes.includes(file.type)) {
  //         setMessagesError(
  //           `File type ${file.type} is not supported. Please select a PDF, Word document, text file, or image.`
  //         );
  //         event.target.value = "";
  //         return;
  //       }

  //       setMessagesError(null);
  //       setSelectedFile(file);

  //       if (file.type.startsWith("image/")) {
  //         const reader = new FileReader();
  //         reader.onload = (e) => setFilePreview(e.target?.result as string);
  //         reader.readAsDataURL(file);
  //       } else {
  //         setFilePreview(null);
  //       }
  //     }
  //   },
  //   [hasPendingMessages]
  // );

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setFilePreview(null);
  }, []);

  // Handle source clicks
  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    // if (shouldUseIframe(url)) {
    setSelectedUrl(url);
    // } else {
    //   setIframeUrl(url);
    // }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!definition.trim() && !messageInput.trim()) {
      alert("Please enter a message before sending.");
      return;
    }

    // If using legacy definition field, transfer to messageInput
    if (definition.trim() && !messageInput.trim()) {
      setMessageInput(definition);
      setDefinition("");
    }

    // Use the new sendMessage function
    sendMessage();
  };

  // Initialize component and load messages ONLY for initial chats
  useEffect(() => {
    if (isInitialChat && !isInitialized) {
      loadInitialMessages();
    } else if (!isInitialChat) {
      // For existing chats, check if there are pending messages and start polling
      const hasPending = messages.some(
        (msg) => msg.responseStatus === "PENDING"
      );
      if (hasPending && !waitingForReply && !pollingInterval) {
        console.log(
          "Found pending messages in existing chat, starting polling..."
        );
        setWaitingForReply(true);
        startPollingForReply(sessionId);
      }
    }
  }, [
    isInitialChat,
    isInitialized,
    loadInitialMessages,
    messages,
    waitingForReply,
    pollingInterval,
    startPollingForReply,
    sessionId,
  ]);

  // Auto-scroll effects - FIXED to only scroll when necessary
  useEffect(() => {
    if (loading && loaderRef.current) {
      loaderRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [loading]);

  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [results]);

  // Only scroll when shouldScrollRef.current is true - prevents continuous scrolling during polling
  useEffect(() => {
    if (shouldScrollRef.current) {
      debouncedScrollToBottom();
    }
  }, [messages, debouncedScrollToBottom]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, [pollingInterval]);

  // Check authentication
  const isAuthenticated = authService.isAuthenticated();
  if (!isAuthenticated) {
    return null;
  }

  const aiMessages = ["8B is Typing", "8B is Thinking", "8B is Retriving"];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % aiMessages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedUrl) {
      setIframeWidth(selectedFile ? "w-[30%]" : "w-[40%]");
    }
  }, [selectedUrl, selectedFile, isOpen]);

  return (
    <div
      className={`min-h-screen flex transition-all duration-500 ease-in-out
    ${isOpen ? "mr-[0vw]" : selectedFile ? "mr-[15vw]" : ""}
    ${selectedFile ? "2xl:gap-x-15" : ""}
    ${selectedUrl ? "2xl:gap-x-30" : ""}
    ${messages.some((msg) => msg.metadata?.fileName) ? "mr-[20vw]" : ""}
  `}
    >
      {/* {(selectedFile || selectedFile || messages.some((msg) => msg.metadata?.fileName)) && (
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute left-0 top-10 z-50 p-2 bg-gray-200 rounded-md shadow hover:bg-gray-300 cursor-pointer"
        >
          {isOpen ? <FaChevronLeft /> : <FaChevronRight />}
        </button>
      )} */}

      {selectedFile ? (
        <div
          className={`left-0 top-12 pt-15 min-h-full  bg-[#FDFDFD]  border border-gray-200 transform transition-transform duration-500 ease-in-out
        ${
          isOpen
            ? " -translate-x-20 "
            : "  -translate-x-[115%] 2xl:-translate-x-[110%] pr-10"
        }
        ${selectedUrl ? "w-[40%] 2xl:w-[35%]" : "w-[40%] 2xl:w-[20%]"}
        ${chatData ? "mt-28 2xl:mt-12" : "-mt-8"}
      `}
        >
          {selectedFile && (
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="absolute top-2  right-0 z-50 p-2 rounded-md hover:bg-gray-100 cursor-pointer"
            >
              <PiSidebarSimpleThin className="h-7 w-7" />
            </button>
          )}
          <div className="p-3  rounded-lg  bg-gray-50">
            <div className="flex items-center  justify-between">
              <div
                className={`w-full gap-2 ${
                  filePreview ? "" : "flex items-center"
                }`}
              >
                {filePreview ? (
                  <img
                    src={filePreview}
                    alt="Preview"
                    className="mt-2 max-w-full h-10 object-contain rounded"
                  />
                ) : (
                  <FiPaperclip className="h-4 w-4 text-gray-500" />
                )}
                <span className="text-sm text-gray-700">
                  {selectedFile.name.length > 15
                    ? `${selectedFile.name.slice(0, 15)}...`
                    : selectedFile.name}
                </span>
                <span className="text-xs text-gray-500">
                  ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </span>
              </div>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="text-gray-400 hover:text-black transition-colors cursor-pointer"
              >
                <FaTimes className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ) : messages?.length > 0 &&
        messages.some((msg) => msg.metadata?.fileName) ? (
        <div
          className={`left-0 top-12 pt-15 min-h-full  bg-[#FDFDFD]  border border-gray-200 transform transition-transform duration-500 ease-in-out
        ${
          isOpen
            ? "-translate-x-20 "
            : " -translate-x-[115%] 2xl:-translate-x-[110%] pr-10"
        }
        ${selectedUrl ? "w-[40%] 2xl:w-[35%]" : "w-[40%] 2xl:w-[20%]"}
        ${chatData ? "mt-28 2xl:mt-12" : "-mt-8"}
      `}
        >
          {messages.some((msg) => msg.metadata?.fileName) && (
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="absolute top-2 right-0 z-50 p-2  rounded-md  hover:bg-gray-100 cursor-pointer"
            >
              <PiSidebarSimpleThin className="h-7 w-7" />
            </button>
          )}
          {messages.map(
            (msg, index) =>
              (msg.metadata?.fileName || msg.s3FileUrl) && (
                <div key={msg.id || index} className=" mb-2">
                  <div className="p-3  bg-gray-50 rounded-lg  flex items-center justify-between min-w-[20%]">
                    <div className="flex items-center gap-2 w-full">
                      <FiPaperclip className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-700">
                        {msg.metadata?.fileName
                          ? msg.metadata?.fileName.length > 15
                            ? `${msg.metadata?.fileName.slice(0, 15)}...`
                            : msg.metadata?.fileName
                          : "Uploaded file"}
                      </span>
                    </div>
                  </div>
                </div>
              )
          )}
        </div>
      ) : null}

      <div
        className={`p-6 gap-y-4 transition-all duration-500 ease-in-out  ${
          selectedUrl ? "w-full max-w-3xl ml-[0vw]" : "w-full max-w-3xl mx-auto"
        }`}
      >
        {/* Error Display */}
        {messagesError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm">
            {messagesError}
            <button
              onClick={() => setMessagesError(null)}
              className="ml-2 text-red-500 hover:text-red-700"
            >
              <FaTimes className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* Render results if available */}
        {(results || chatData || messages.length > 0) && (
          <div className="flex flex-col items-center justify-center mb-6">
            <p className="text-sm text-gray-500 mb-2">
              {new Date(Date.now()).toLocaleString()}
            </p>
            <p className="text-sm text-gray-500 mb-2">Powered by 8byte.ai</p>
          </div>
        )}

        {results || chatData || messages.length > 0 ? (
          <div className="flex flex-col items-center">
            <div className="max-h-[70vh] overflow-auto scrollbar-hide w-full px-2">
              {(chatData || messages.length > 0) && (
                <div className="space-y-6 w-full">
                  {messages.map((msg, index) => (
                    <div key={msg.id || index}>
                      <div ref={topRef} />

                      {/* User Query */}
                      <p className="text-md flex justify-end text-gray-800">
                        <span className="text-gray-800 max-w-100 px-6 py-2 mb-2 rounded-2xl rounded-tr-none bg-gray-100 shadow-md">
                          {msg.query}
                        </span>
                      </p>

                      {/* File attachment indicator */}
                      {(msg.metadata?.fileName || msg.s3FileUrl) && (
                        <div className="flex justify-end mb-2">
                          <div className="max-w-100 px-6 py-2 bg-gray-100 rounded-lg shadow-sm">
                            <div className="flex items-center gap-2">
                              <FiPaperclip className="h-4 w-4" />
                              <span className="text-xs">
                                {msg.metadata?.fileName || "Uploaded file"}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Web search indicator */}
                      {msg.isWebSearch && (
                        <div className="flex justify-end mb-2">
                          <div className="max-w-100 px-6 py-2 bg-blue-50 rounded-lg shadow-sm">
                            <div className="flex items-center gap-1">
                              <GoGlobe className="h-3 w-3" />
                              <span className="text-xs opacity-70">
                                Web Search Enabled
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* AI Response - Success */}
                      {msg.responseStatus === "SUCCESS" && msg.result && (
                        <div className=" rounded-2xl p-6 transition ">
                          <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl flex items-center capitalize font-semibold text-gray-800">
                              <img
                                src="https://s3-qa-8byte.s3.ap-south-1.amazonaws.com/8Black.png"
                                alt=""
                                className="h-5 w-5 mr-2"
                              />{" "}
                              {msg.query}
                            </h2>
                          </div>

                          <div className="text-gray-700 leading-7">
                            <ChatMarkdown
                              msg={msg}
                              handleSourceClick={handleSourceClick}
                            />
                          </div>

                          {msg.metadata && (
                            <div className="mt-4 text-xs text-gray-500">
                              <p>
                                Created:{" "}
                                {new Date(msg.createdAt).toLocaleString()}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* AI Response - Pending (show "AI is processing") */}
                      {msg.responseStatus === "PENDING" && !waitingForReply && (
                        <div className="bg-white shadow-md rounded-2xl p-6 transition hover:shadow-lg">
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="text-sm">8B is processing</span>
                            <span className="flex space-x-1">
                              <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" />
                              <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce [animation-delay:0.2s]" />
                              <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce [animation-delay:0.4s]" />
                            </span>
                          </div>
                          <p className="text-xs mt-2 text-gray-500">
                            Processing •{" "}
                            {new Date(msg.createdAt).toLocaleTimeString([], {
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                      )}

                      {/* AI Response - Failure */}
                      {msg.responseStatus === "FAILURE" && (
                        <div className="bg-red-50 border border-red-200 shadow-md rounded-2xl p-6">
                          <p className="text-sm text-red-900">
                            Failed to get AI response
                          </p>
                          <p className="text-xs mt-1 text-red-600">
                            Error •{" "}
                            {new Date(
                              msg.updatedAt || msg.createdAt
                            ).toLocaleTimeString([], {
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Waiting for reply indicator (show "AI is typing") */}
                  {waitingForReply && (
                    <div className="bg-white shadow-md rounded-2xl p-6">
                      <div className="flex items-center gap-2 text-gray-600">
                        <span className="text-sm">{aiMessages[index]}</span>
                        <span className="flex space-x-1">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0.2s]" />
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0.4s]" />
                        </span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
              <ResultsCard results={allResults} onViewSource={setSelectedUrl} />
            </div>
          </div>
        ) : (
          <SuggestedQuestions
            onQuestionClick={(q) => {
              setDefinition(q);
              setMessageInput(q);
            }}
          />
        )}

        {/* Loading Indicator */}
        {loading && (
          <div
            ref={loaderRef}
            className="flex items-center mt-10 gap-3 text-gray-600"
          >
            <div className="relative w-4 h-4">
              <div className="absolute inset-0 rounded-full border-2 border-blue-600 border-t-transparent animate-spin"></div>
              <div className="absolute inset-1 rounded-full bg-white"></div>
            </div>
            <span className="text-sm font-light">Thinking...</span>
          </div>
        )}

        {/* Input Form */}
        <form
          onSubmit={onSubmit}
          className="sticky bottom-0 py-2 w-full px-4 mb-4 backdrop-blur-md bg-white/60 transition-all duration-500 ease-in-out"
        >
          {/* File Preview */}
          {selectedFile && (
            <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {filePreview ? (
                    <img
                      src={filePreview}
                      alt="Preview"
                      className="mt-2 max-w-full h-10 object-contain rounded"
                    />
                  ) : (
                    <FiPaperclip className="h-4 w-4 text-gray-500" />
                  )}
                  <span className="  text-sm text-gray-700">
                    {selectedFile.name.length > 15
                      ? `${selectedFile.name.slice(0, 15)}...`
                      : selectedFile.name}
                  </span>
                  <span className="text-xs text-gray-500">
                    ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="text-gray-400 hover:text-black transition-colors cursor-pointer"
                >
                  <FaTimes className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          <div className="items-center bg-white border border-gray-300 rounded-3xl shadow-md px-4 py-3 relative">
            <div className="flex items-center">
              {/* Input field */}
              <input
                type="text"
                placeholder={
                  hasPendingMessages
                    ? "Waiting for response..."
                    : "Ask a question..."
                }
                className="flex-1 bg-transparent focus:outline-none text-gray-800 disabled:text-gray-500"
                value={
                  chatData || messages.length > 0 ? messageInput : definition
                }
                onChange={(e) => {
                  if (chatData || messages.length > 0) {
                    setMessageInput(e.target.value);
                  } else {
                    setDefinition(e.target.value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    onSubmit(e as any);
                  }
                }}
                disabled={hasPendingMessages || loading}
              />

              {/* Submit button */}
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 ml-3 disabled:opacity-60 cursor-pointer disabled:cursor-not-allowed"
                disabled={sendingMessage || hasPendingMessages || loading}
              >
                {sendingMessage || loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L12 6.414V17a1 1 0 11-2 0V6.414L4.707 10.707a1 1 0 01-1.414-1.414l6-6z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            </div>

            {/* Tags (Web Search and file upload) */}
            <div className="flex items-center mt-2 gap-4">
              <button
                type="button"
                onClick={() => setWebSearch(!websearch)}
                disabled={hasPendingMessages || loading}
                className={`text-xs px-3 py-2 flex items-center rounded-full ${
                  websearch ? "bg-gray-100" : ""
                } shadow-sm border border-gray-200 text-black cursor-pointer transition-colors duration-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <GoGlobe
                  className={`h-4 w-4 ${
                    websearch ? "text-blue-500" : "text-black"
                  } mr-1`}
                />
                Web Search
              </button>

              {/* File Upload Button */}
              {/* <label
                className={`text-xs px-3 py-2 flex items-center rounded-full shadow-sm border border-gray-200 text-black transition-colors duration-200 hover:bg-gray-100 ${
                  hasPendingMessages || loading
                    ? "opacity-50 cursor-not-allowed"
                    : "cursor-pointer"
                }`}
              >
                <FiPaperclip className="h-4 w-4 mr-1" />
                Attach File
                <input
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.svg"
                  disabled={hasPendingMessages || loading}
                />
              </label> */}
            </div>
          </div>

          {/* Disclaimer */}
          <p className="text-xs text-gray-400 text-center mt-2">
            AI may produce inaccurate information. Please refer to source
            material to verify.
          </p>
        </form>
      </div>

      {/* URL Handling */}
      {selectedUrl ? (
        <div
          className={`w-2/3  transition-transform  duration-500 ease-in-out  `}
        >
          <div className="hidden sticky lg:block">
            <IframeViewer
              width={iframeWidth || "w-[40%]"}
              url={selectedUrl}
              onClose={() => setSelectedUrl("")}
            />
          </div>
        </div>
      ) : selectedUrl ? (
        (() => {
          window.open(selectedUrl, "_blank", "noopener,noreferrer");
          setSelectedUrl("");
          return null;
        })()
      ) : null}

      {/* Iframe Modal */}
      {/* {iframeUrl && (
        <div 
      
        className={`w-2/3  transition-transform  duration-500 ease-in-out  `}
  >
        <div className="hidden sticky lg:block">
                  <IframeViewer url={iframeUrl} width="w-[30%]" onClose={() => setIframeUrl(null)} />

        </div>
      </div>
      )} */}
    </div>
  );
};

export default QueryInputBox;
